import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import { BundleData, type CreateBundlePayload, type UpdateBundlePayload } from './model'

type BundleListApiResponse = {
  data: Record<string, unknown>[]
  pagination: { total: number; page: number; pageSize: number }
}

/**
 * Per-request override of the sidebar branch the axios interceptor would
 * otherwise send. Omitted entirely when the payload carries no branch, so the
 * interceptor's value stands.
 */
const branchHeader = (branchId?: number) =>
  branchId == null ? undefined : { headers: { 'branch-id': String(branchId) } }

export const BundleService = (client: AxiosInstance) => {
  return {
    /** `isActive: false` returns archived bundles, mirroring the packages list. */
    async getBundleList(
      opt: PaginationOpt,
      isActive?: boolean
    ): Promise<PaginationResponse<BundleData>> {
      const { data } = await wrapApiCall(() =>
        client.get<BundleListApiResponse>('/bundles', {
          params: { ...opt, ...(isActive === undefined ? {} : { isActive }) },
        })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, BundleData)),
      }
    },

    async getBundleById(id: number | string): Promise<BundleData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/bundles/${id}`)
      )
      return deserialize(data, BundleData)
    },

    /**
     * The BE requires the body's `branchId` to equal the `branch-id` header
     * (BundleBranchMismatchException otherwise), so a bundle built for a branch
     * other than the sidebar's current one must send that branch as the header.
     */
    async createBundle(payload: CreateBundlePayload): Promise<BundleData> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/bundles', payload, branchHeader(payload.branchId))
      )
      return deserialize(data, BundleData)
    },

    /**
     * Same branch rule as [[createBundle]], and stricter: the BE resolves the
     * bundle by `branch-id` first, so editing one while the sidebar sits on
     * another branch 404s before the mismatch check is even reached. A partial
     * patch that carries no `branchId` (the archive/restore toggles) keeps the
     * interceptor's sidebar branch, which is the row's own branch there.
     */
    async updateBundleById(id: number | string, payload: UpdateBundlePayload): Promise<BundleData> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(
          `/bundles/${id}`,
          payload,
          branchHeader(payload.branchId)
        )
      )
      return deserialize(data, BundleData)
    },

    async deleteBundleById(id: number | string): Promise<void> {
      await wrapApiCall(() => client.delete(`/bundles/${id}`))
    },
  }
}
